import { PACKS_EDITABLE } from './player-packs-choice';

// What a store sells on its personalization (teamStores.personalization.mode).
// Display only: PUT /api/team-stores/:id/personalization-mode stays the
// authority. Same four as ghostfit-console/lib/store-personalization-font.js;
// absent or unknown reads as both.
export type PersonalizationMode = 'both' | 'numbers_only' | 'names_only' | 'off';

export const PERSONALIZATION_MODE_OPTIONS: ReadonlyArray<{ value: PersonalizationMode; label: string }> = [
  { value: 'both', label: 'Names and numbers' },
  { value: 'numbers_only', label: 'Numbers only' },
  { value: 'names_only', label: 'Names only' },
  { value: 'off', label: 'Off' },
];

export type ModeStore = {
  status?: string;
  personalization?: { hanger?: boolean; mode?: string; [k: string]: unknown } | null;
  playerPacks?: { enabled?: boolean } | null;
  playerPackPlan?: unknown;
  playerPacksBuild?: { status?: string } | null;
  preStaging?: { playerPacksBuild?: { status?: string } | null } | null;
  liveAt?: unknown;
  productsCreatedAt?: unknown;
};

export function modeOf(store: ModeStore): PersonalizationMode {
  const mode = store.personalization?.mode;
  return PERSONALIZATION_MODE_OPTIONS.some((o) => o.value === mode) ? (mode as PersonalizationMode) : 'both';
}

// Same rule as sellsPlayerPacks in ghostfit-console/lib/personalization-mode-save.js.
function sellsPlayerPacks(store: ModeStore): boolean {
  if (store.playerPacksBuild?.status === 'built') return true;
  const pin = store.playerPacks?.enabled;
  if (pin === true) return true;
  if (pin === false || store.liveAt || store.productsCreatedAt) return false;
  return store.playerPackPlan != null || store.preStaging?.playerPacksBuild?.status === 'built';
}

export function modeSelectState(store: ModeStore) {
  const building = store.status === 'creating_products';
  const offDisabled = sellsPlayerPacks(store);
  const packsFixed = !PACKS_EDITABLE.has(store.status || '') || store.playerPacksBuild?.status === 'built';
  let hint = 'Takes effect on the live store right away.';
  if (building) hint = 'Being built. Set this when the build finishes.';
  else if (store.personalization?.hanger !== true) hint = 'Built without personalization: turning it on needs a rebuild.';
  else if (offDisabled) {
    hint = packsFixed
      ? "Player Packs are on and can't be changed after the build, so Off isn't available."
      : 'Set Player Packs to Off before choosing Off.';
  }
  return { value: modeOf(store), locked: building, offDisabled, hint };
}

export type ModeSaveResult = { message?: string; built?: boolean; shopifySynced?: boolean };

// The doc holds the new mode while the live storefront still has the old one.
// An unbuilt store has no storefront to update, so that is a normal save.
export function storefrontNotUpdated(res: ModeSaveResult): boolean {
  return res.built === true && res.shopifySynced === false;
}

// What Create Store sends. With the switch on, the select always builds the
// ladder (hanger:true, Off included) so the store can be turned on later;
// hanger:false stays API-only. With the switch off, the old checkbox as before.
export function createStorePersonalization({ controlsOn, hanger, mode }: {
  controlsOn: boolean; hanger: boolean; mode: PersonalizationMode;
}): { hanger: boolean; mode?: PersonalizationMode } {
  return controlsOn ? { hanger: true, mode } : { hanger };
}
